import type {
  AddOnsState,
  PlanAction,
  PlanLadderState,
  PlanOption,
  ShopPlanInfo,
} from '../types/billing.types';

/**
 * The ladder is resolved on the server (`plan-ladder.util.ts`) so the pricing
 * page cannot offer a purchase that would silently downgrade the merchant.
 *
 * These helpers only cover the case where the server did not send it — an
 * older deployed backend, or the 404 default the API client synthesises. The
 * fallback is derived from the plan code we do have rather than defaulting
 * every card to "subscribe", because that default is exactly the bug C1 fixes.
 */

const LADDER = ['launch', 'growth', 'scale'] as const;

function labelFor(action: PlanAction, planCode: string): string {
  const name = planCode.charAt(0).toUpperCase() + planCode.slice(1);
  switch (action) {
    case 'current':
      return 'Current plan';
    case 'upgrade':
      return `Upgrade to ${name}`;
    case 'downgrade':
      return `Switch to ${name}`;
    case 'unavailable':
      return 'Unavailable';
    default:
      return `Start with ${name}`;
  }
}

/** Mirror of the backend resolver, used only when the server omitted it. */
export function deriveLadderFallback(currentPlanCode: string | null): PlanLadderState {
  const currentRank = currentPlanCode ? LADDER.indexOf(currentPlanCode as never) : -1;
  const unknownActive = currentPlanCode !== null && currentRank === -1;

  const options: PlanOption[] = LADDER.map((code) => {
    let action: PlanAction;
    if (unknownActive) action = 'unavailable';
    else if (currentPlanCode === null) action = 'subscribe';
    else if (code === currentPlanCode) action = 'current';
    else action = LADDER.indexOf(code) > currentRank ? 'upgrade' : 'downgrade';

    return {
      code,
      action,
      cta_label: labelFor(action, code),
      purchasable: action !== 'current' && action !== 'unavailable',
    };
  });

  const nextPlanCode =
    !unknownActive && currentRank >= 0 && currentRank < LADDER.length - 1
      ? LADDER[currentRank + 1]
      : null;

  return {
    current_plan_code: currentPlanCode,
    has_upgrade_available: nextPlanCode !== null,
    next_plan_code: nextPlanCode,
    options,
  };
}

export function getPlanLadder(plan: ShopPlanInfo | null): PlanLadderState {
  if (plan?.plan_ladder) return plan.plan_ladder;
  return deriveLadderFallback(plan?.plan?.code ?? null);
}

export function getPlanOption(ladder: PlanLadderState, code: string): PlanOption | null {
  return ladder.options.find((o) => o.code === code) ?? null;
}

/**
 * Add-on availability, falling back to "not available" when the server did not
 * report it. Hiding a pack the merchant could have bought is recoverable;
 * offering one the checkout will reject is not.
 */
export function getAddOns(plan: ShopPlanInfo | null): AddOnsState {
  if (plan?.add_ons) return plan.add_ons;
  return {
    extra_products: {
      available: false,
      reason: 'Add-ons are available once you are on a paid plan.',
      price: 29,
      credits: 3,
    },
    premium_generation: {
      available: false,
      reason: 'Add-ons are available once you are on a paid plan.',
      price: 15,
      credits: 1,
    },
  };
}

/**
 * Proration note shown on an upgrade CTA. MONETIZATION_STRATEGY.md §5 calls
 * this out explicitly: merchants defer upgrades because they assume they will
 * pay twice, and Shopify in fact prorates mid-cycle changes.
 */
export const UPGRADE_PRORATION_NOTE =
  "You'll only be charged the difference for the rest of this billing cycle.";
